import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import {
  addTmRisk,
  removeTmRisk,
  selectTmRisks,
  setTmRisks,
  updateTmRisk,
} from '../store/tmRisk/tmRiskSlice.ts';
import {
  selectTmActors,
  setTmActors,
} from '../store/tmActor/tmActorSlice.ts';
import {
  selectTmMitigations,
  setTmMitigations,
} from '../store/tmMitigation/tmMitigationSlice.ts';
import { selectSelectedTmAssessmentId } from '../store/tmAssessment/tmAssessmentSlice.ts';
import {
  createTmRisk,
  deleteTmRiskById,
  listTmActors,
  listTmMitigations,
  listTmRisks,
  updateTmRiskById,
  createTmActor,
  createTmMitigation,
} from '../../../api.ts';
import { TmRisk, TmRiskUpdate } from '../models/tmRisk.ts';
import { TmActor } from '../models/tmActor.ts';
import { addTmActor } from '../store/tmActor/tmActorSlice.ts';
import { addTmMitigation } from '../store/tmMitigation/tmMitigationSlice.ts';

interface RiskFormState {
  description: string;
  explanation: string;
  chance: number;
  impact: number;
  chance_after_mitigation: number;
  impact_after_mitigation: number;
  status: string;
  actorId: number | '';
  mitigationIds: number[];
}

const defaultFormState: RiskFormState = {
  description: '',
  explanation: '',
  chance: 0,
  impact: 0,
  chance_after_mitigation: 0,
  impact_after_mitigation: 0,
  status: 'open',
  actorId: '',
  mitigationIds: [],
};

const scoreInputProps = { min: 0, max: 5 };

const TmRiskTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const risks = useAppSelector(selectTmRisks);
  const actors = useAppSelector(selectTmActors);
  const mitigations = useAppSelector(selectTmMitigations);
  const currentOrg = useAppSelector(selectCurrentOrganization);
  const selectedAssessmentId = useAppSelector(selectSelectedTmAssessmentId);
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<RiskFormState>(defaultFormState);
  const [editingRisk, setEditingRisk] = useState<TmRisk | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for creating new actor on the fly
  const [actorDialogOpen, setActorDialogOpen] = useState(false);
  const [newActorName, setNewActorName] = useState('');

  // State for creating new mitigation on the fly
  const [mitigationDialogOpen, setMitigationDialogOpen] = useState(false);
  const [newMitigationForm, setNewMitigationForm] = useState({
    description: '',
    status: 'proposed',
    priority: 0,
    effort: 0,
  });

  const canEdit = currentOrg?.role && currentOrg.role !== 'viewer';
  const assessmentMissing = !selectedAssessmentId;

  const loadRisks = useCallback(async () => {
    if (!selectedAssessmentId) {
      dispatch(setTmRisks([]));
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await listTmRisks(selectedAssessmentId);
      dispatch(setTmRisks(data));
    } catch (err: any) {
      console.error('Failed to load risks', err);
      setError(err?.response?.data?.detail || 'Failed to load risks');
    } finally {
      setLoading(false);
    }
  }, [dispatch, selectedAssessmentId]);

  const loadActors = useCallback(async () => {
    if (!selectedAssessmentId) {
      dispatch(setTmActors([]));
      return;
    }

    try {
      const data = await listTmActors(selectedAssessmentId);
      dispatch(setTmActors(data));
    } catch (err) {
      console.error('Failed to load actors', err);
    }
  }, [dispatch, selectedAssessmentId]);

  const loadMitigations = useCallback(async () => {
    if (!selectedAssessmentId) {
      dispatch(setTmMitigations([]));
      return;
    }

    try {
      const data = await listTmMitigations(selectedAssessmentId);
      dispatch(setTmMitigations(data));
    } catch (err) {
      console.error('Failed to load mitigations', err);
    }
  }, [dispatch, selectedAssessmentId]);

  useEffect(() => {
    loadRisks();
    loadActors();
    loadMitigations();
  }, [loadRisks, loadActors, loadMitigations]);

  const openDialog = (risk?: TmRisk) => {
    if (risk) {
      setEditingRisk(risk);
      setFormState({
        description: risk.description,
        explanation: risk.explanation,
        chance: risk.chance,
        impact: risk.impact,
        chance_after_mitigation: risk.chance_after_mitigation,
        impact_after_mitigation: risk.impact_after_mitigation,
        status: risk.status,
        actorId: risk.actor?.id ?? risk.actor_id ?? '',
        mitigationIds:
          risk.mitigation_ids ??
          risk.mitigations?.map(m => m.id).filter((id): id is number => id !== undefined) ??
          [],
      });
    } else {
      setEditingRisk(null);
      setFormState(defaultFormState);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRisk(null);
    setFormState(defaultFormState);
  };

  const formToRiskPayload = (): Omit<TmRisk, 'id'> => ({
    description: formState.description,
    explanation: formState.explanation,
    chance: formState.chance,
    impact: formState.impact,
    chance_after_mitigation: formState.chance_after_mitigation,
    impact_after_mitigation: formState.impact_after_mitigation,
    status: formState.status,
    assessment_id: selectedAssessmentId!,
    actor_id: formState.actorId === '' ? null : formState.actorId,
    mitigation_ids: formState.mitigationIds,
  });

  const formToUpdatePayload = (): TmRiskUpdate => ({
    description: formState.description,
    explanation: formState.explanation,
    chance: formState.chance,
    impact: formState.impact,
    chance_after_mitigation: formState.chance_after_mitigation,
    impact_after_mitigation: formState.impact_after_mitigation,
    status: formState.status,
    assessment_id: selectedAssessmentId!,
    actor_id: formState.actorId === '' ? null : formState.actorId,
    mitigation_ids: formState.mitigationIds,
  });

  const handleSave = async () => {
    if (!selectedAssessmentId) {
      setError('Select or create an assessment first');
      return;
    }

    if (!formState.description.trim()) {
      setError('Description is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingRisk?.id) {
        const updated = await updateTmRiskById(editingRisk.id, formToUpdatePayload());
        dispatch(updateTmRisk(updated));
      } else {
        const created = await createTmRisk(formToRiskPayload());
        dispatch(addTmRisk(created));
      }

      closeDialog();
    } catch (err: any) {
      console.error('Failed to save risk', err);
      setError(err?.response?.data?.detail || 'Failed to save risk');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (risk: TmRisk) => {
    if (!risk.id) return;
    if (!window.confirm(`Delete risk "${risk.description}"?`)) return;

    try {
      setLoading(true);
      setError(null);
      await deleteTmRiskById(risk.id);
      dispatch(removeTmRisk(risk.id));
    } catch (err: any) {
      console.error('Failed to delete risk', err);
      setError(err?.response?.data?.detail || 'Failed to delete risk');
    } finally {
      setLoading(false);
    }
  };

  const actorLookup = useMemo(
    () =>
      actors.reduce<Record<number, TmActor>>((acc, actor) => {
        if (actor.id !== undefined) acc[actor.id] = actor;
        return acc;
      }, {}),
    [actors]
  );

  const handleCreateActor = async () => {
    if (!selectedAssessmentId) {
      setError('Select or create an assessment first');
      return;
    }

    if (!newActorName.trim()) {
      setError('Actor name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const createdActor = await createTmActor({ name: newActorName, assessment_id: selectedAssessmentId });
      dispatch(addTmActor(createdActor));

      // Auto-select the newly created actor
      if (createdActor.id) {
        setFormState(prev => ({ ...prev, actorId: createdActor.id! }));
      }

      // Close dialog and reset
      setActorDialogOpen(false);
      setNewActorName('');
    } catch (err: any) {
      console.error('Failed to create actor', err);
      setError(err?.response?.data?.detail || 'Failed to create actor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMitigation = async () => {
    if (!selectedAssessmentId) {
      setError('Select or create an assessment first');
      return;
    }

    if (!newMitigationForm.description.trim()) {
      setError('Mitigation description is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const createdMitigation = await createTmMitigation({
        ...newMitigationForm,
        assessment_id: selectedAssessmentId,
      });
      dispatch(addTmMitigation(createdMitigation));

      // Auto-select the newly created mitigation
      if (createdMitigation.id) {
        setFormState(prev => ({
          ...prev,
          mitigationIds: [...prev.mitigationIds, createdMitigation.id!],
        }));
      }

      // Close dialog and reset
      setMitigationDialogOpen(false);
      setNewMitigationForm({
        description: '',
        status: 'proposed',
        priority: 0,
        effort: 0,
      });
    } catch (err: any) {
      console.error('Failed to create mitigation', err);
      setError(err?.response?.data?.detail || 'Failed to create mitigation');
    } finally {
      setLoading(false);
    }
  };

  const scoreChip = (score: number) => {
    const getColors = () => {
      if (score >= 4) {
        return { bg: theme.palette.error.light, color: theme.palette.error.contrastText };
      }
      if (score >= 2) {
        return { bg: theme.palette.warning.light, color: theme.palette.warning.contrastText };
      }
      return { bg: theme.palette.success.light, color: theme.palette.success.contrastText };
    };
    const { bg, color } = getColors();
    return (
      <Chip
        label={score}
        size="small"
        sx={{
          minWidth: 48,
          textAlign: 'center',
          fontWeight: 600,
          backgroundColor: bg,
          color,
        }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Risks</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openDialog()}
          disabled={!canEdit || assessmentMissing}
        >
          Add Risk
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {assessmentMissing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select or create an assessment to view and edit risks.
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Description
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Actor
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Chance
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Impact
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Residual Chance
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Residual Impact
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Mitigations
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  position: 'sticky',
                  right: 0,
                  fontWeight: 'bold',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  zIndex: 2,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {risks.map(risk => (
              <TableRow key={risk.id ?? risk.description}>
                <TableCell>
                  <Typography fontWeight={600}>{risk.description}</Typography>
                  {risk.explanation && (
                    <Typography variant="body2" color="text.secondary">
                      {risk.explanation}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{risk.status}</TableCell>
                <TableCell>{risk.actor?.name ?? actorLookup[risk.actor_id ?? -1]?.name ?? '—'}</TableCell>
                <TableCell>{scoreChip(risk.chance)}</TableCell>
                <TableCell>{scoreChip(risk.impact)}</TableCell>
                <TableCell>{scoreChip(risk.chance_after_mitigation)}</TableCell>
                <TableCell>{scoreChip(risk.impact_after_mitigation)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(risk.mitigations ?? []).map(mitigation => (
                      <Chip
                        key={mitigation.id ?? mitigation.description}
                        label={mitigation.description}
                        size="small"
                      />
                    ))}
                    {(risk.mitigations?.length ?? 0) === 0 && '—'}
                  </Stack>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    position: 'sticky',
                    right: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => openDialog(risk)}
                    disabled={!canEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(risk)}
                    disabled={!canEdit}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {risks.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {assessmentMissing
                    ? 'Select an assessment to view risks'
                    : loading
                      ? 'Loading risks...'
                      : 'No risks found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingRisk ? 'Edit Risk' : 'Add Risk'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Description"
            value={formState.description}
            onChange={e => setFormState(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Explanation"
            value={formState.explanation}
            onChange={e => setFormState(prev => ({ ...prev, explanation: e.target.value }))}
            fullWidth
            margin="normal"
            multiline
            minRows={3}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Chance"
              type="number"
              value={formState.chance}
              onChange={e => setFormState(prev => ({ ...prev, chance: Number(e.target.value) }))}
              inputProps={scoreInputProps}
              fullWidth
            />
            <TextField
              label="Impact"
              type="number"
              value={formState.impact}
              onChange={e => setFormState(prev => ({ ...prev, impact: Number(e.target.value) }))}
              inputProps={scoreInputProps}
              fullWidth
            />
            <TextField
              label="Residual Chance"
              type="number"
              value={formState.chance_after_mitigation}
              onChange={e =>
                setFormState(prev => ({ ...prev, chance_after_mitigation: Number(e.target.value) }))
              }
              inputProps={scoreInputProps}
              fullWidth
            />
            <TextField
              label="Residual Impact"
              type="number"
              value={formState.impact_after_mitigation}
              onChange={e =>
                setFormState(prev => ({ ...prev, impact_after_mitigation: Number(e.target.value) }))
              }
              inputProps={scoreInputProps}
              fullWidth
            />
          </Stack>
          <TextField
            label="Status"
            value={formState.status}
            onChange={e => setFormState(prev => ({ ...prev, status: e.target.value }))}
            fullWidth
            margin="normal"
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="risk-actor-label">Actor</InputLabel>
              <Select
                labelId="risk-actor-label"
                value={formState.actorId === '' ? '' : formState.actorId}
                label="Actor"
                onChange={e => {
                  const value = e.target.value as string;
                  setFormState(prev => ({
                    ...prev,
                    actorId: value === '' ? ('' as const) : Number(value),
                  }));
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {actors.map(actor => (
                  <MenuItem key={actor.id ?? actor.name} value={actor.id ?? ''}>
                    {actor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setActorDialogOpen(true)}
              disabled={!canEdit}
              sx={{ mt: 2, minWidth: 140 }}
            >
              New Actor
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="risk-mitigations-label">Mitigations</InputLabel>
              <Select<string[]>
                labelId="risk-mitigations-label"
                multiple
                value={formState.mitigationIds.map(String)}
                onChange={(event: SelectChangeEvent<string[]>) => {
                  const value = typeof event.target.value === 'string'
                    ? event.target.value.split(',')
                    : event.target.value;
                  setFormState(prev => ({
                    ...prev,
                    mitigationIds: value.map(Number),
                  }));
                }}
                input={<OutlinedInput label="Mitigations" />}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selected.map(value => {
                      const mitigation = mitigations.find(m => m.id?.toString() === value);
                      return (
                        <Chip
                          key={value}
                          label={mitigation?.description ?? value}
                          size="small"
                          onDelete={(e) => {
                            e.stopPropagation();
                            setFormState(prev => ({
                              ...prev,
                              mitigationIds: prev.mitigationIds.filter(id => id.toString() !== value),
                            }));
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      );
                    })}
                  </Stack>
                )}
              >
                {mitigations.map(mitigation => (
                  <MenuItem key={mitigation.id ?? mitigation.description} value={mitigation.id ?? ''}>
                    {mitigation.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setMitigationDialogOpen(true)}
              disabled={!canEdit}
              sx={{ mt: 2, minWidth: 170 }}
            >
              New Mitigation
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading || !canEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for creating new actor */}
      <Dialog
        open={actorDialogOpen}
        onClose={() => {
          setActorDialogOpen(false);
          setNewActorName('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create New Actor</DialogTitle>
        <DialogContent>
          <TextField
            label="Actor Name"
            value={newActorName}
            onChange={e => setNewActorName(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActorDialogOpen(false);
              setNewActorName('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateActor} variant="contained" disabled={loading || !canEdit}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for creating new mitigation */}
      <Dialog
        open={mitigationDialogOpen}
        onClose={() => {
          setMitigationDialogOpen(false);
          setNewMitigationForm({
            description: '',
            status: 'proposed',
            priority: 0,
            effort: 0,
          });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create New Mitigation</DialogTitle>
        <DialogContent>
          <TextField
            label="Description"
            value={newMitigationForm.description}
            onChange={e =>
              setNewMitigationForm(prev => ({ ...prev, description: e.target.value }))
            }
            fullWidth
            margin="normal"
            required
            autoFocus
            multiline
            minRows={2}
          />
          <TextField
            label="Status"
            value={newMitigationForm.status}
            onChange={e => setNewMitigationForm(prev => ({ ...prev, status: e.target.value }))}
            fullWidth
            margin="normal"
          />
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Priority"
              type="number"
              value={newMitigationForm.priority}
              onChange={e =>
                setNewMitigationForm(prev => ({ ...prev, priority: Number(e.target.value) }))
              }
              inputProps={{ min: 0, max: 5 }}
              fullWidth
            />
            <TextField
              label="Effort"
              type="number"
              value={newMitigationForm.effort}
              onChange={e =>
                setNewMitigationForm(prev => ({ ...prev, effort: Number(e.target.value) }))
              }
              inputProps={{ min: 0, max: 5 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMitigationDialogOpen(false);
              setNewMitigationForm({
                description: '',
                status: 'proposed',
                priority: 0,
                effort: 0,
              });
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateMitigation} variant="contained" disabled={loading || !canEdit}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TmRiskTable;
